import type { TenantListItem } from "../../../models/users";

/**
 * Sentinel de form/filtro para Grups. Al guardar, `toApiFeaturedBadgeTenantId` lo
 * traduce al UUID real del tenant Grups, distinto de `null` (= todos los tenants).
 */
export const GRUPS_RULES_TENANT_ID = "grups";

/** Sentinel del filtro: muestra reglas de todos los tenants. */
export const ALL_RULES_TENANTS_FILTER_ID = "__ALL_TENANTS__";

export const allRulesTenantsFilterOption = {
  id: ALL_RULES_TENANTS_FILTER_ID,
  provider: "Todos",
};

export const ownedRulesTenantIdForAdmin = (
  role: string | undefined,
  tenantId: string | null | undefined
): string | null =>
  (role === "TENANT_ADMIN" || role === "SUPER_ADMIN") && tenantId ? tenantId : null;

export const isRulesTenantOwnedByAdmin = (
  rowTenantId: string | null | undefined,
  ownedTenantId: string | null | undefined
): boolean => ownedTenantId == null || rowTenantId === ownedTenantId;

export const applyOwnedRulesTenantId = <T extends { tenantId?: string }>(
  values: T,
  ownedTenantId: string | null | undefined
): T => {
  if (!ownedTenantId) {
    return values;
  }

  return { ...values, tenantId: ownedTenantId };
};

export const buildRulesTenantOptions = (
  tenants: Array<{ id: string; provider: string }> | undefined,
  includeAllOption: boolean,
  allOption: { id: string; provider: string } = allRulesTenantsFilterOption
): Array<{ id: string; provider: string }> => {
  const list = Array.isArray(tenants) ? tenants : [];
  return includeAllOption ? [allOption, ...list] : list;
};

export const isAllRulesTenantsFilter = (tenantId?: string | null): boolean =>
  tenantId == null || tenantId === ALL_RULES_TENANTS_FILTER_ID;

export const findGrupsTenantIdFromList = (
  tenants: TenantListItem[] | undefined
): string | undefined => {
  const list = Array.isArray(tenants) ? tenants : [];

  return (
    list.find((tenant) => tenant.id?.toLowerCase() === GRUPS_RULES_TENANT_ID)?.id ??
    list.find((tenant) => tenant.provider?.trim().toLowerCase() === GRUPS_RULES_TENANT_ID)?.id
  );
};

export const toDropdownTenantId = (
  tenantId: string | undefined,
  grupsTenantId: string | undefined
): string | undefined => {
  if (!tenantId || tenantId === GRUPS_RULES_TENANT_ID) {
    return grupsTenantId;
  }

  return tenantId;
};

export const fromDropdownTenantId = (
  tenantId: string | undefined,
  grupsTenantId: string | undefined
): string | undefined => {
  if (!tenantId) {
    return undefined;
  }

  if (grupsTenantId && tenantId === grupsTenantId) {
    return GRUPS_RULES_TENANT_ID;
  }

  return tenantId;
};

export const toRulesFilterDropdownTenantId = (
  tenantId: string | undefined,
  grupsTenantId: string | undefined
): string => {
  if (isAllRulesTenantsFilter(tenantId)) {
    return ALL_RULES_TENANTS_FILTER_ID;
  }

  return toDropdownTenantId(tenantId, grupsTenantId) ?? ALL_RULES_TENANTS_FILTER_ID;
};

export const fromRulesFilterDropdownTenantId = (
  tenantId: string | undefined,
  grupsTenantId: string | undefined
): string | undefined => {
  if (!tenantId || tenantId === ALL_RULES_TENANTS_FILTER_ID) {
    return undefined;
  }

  return fromDropdownTenantId(tenantId, grupsTenantId);
};

export const toApiRulesTenantId = (formTenantId: string | undefined): string | null =>
  !formTenantId || formTenantId === GRUPS_RULES_TENANT_ID
    ? null
    : formTenantId;

export const toFormRulesTenantId = (tenantId: string | null | undefined): string =>
  tenantId == null ? GRUPS_RULES_TENANT_ID : tenantId;

export const matchesRulesTenantFilter = (
  rowTenantId: string | null | undefined,
  filterTenantId: string,
  grupsTenantId?: string
): boolean => {
  if (isAllRulesTenantsFilter(filterTenantId)) {
    return true;
  }

  if (filterTenantId === GRUPS_RULES_TENANT_ID) {
    return (
      rowTenantId == null ||
      rowTenantId === GRUPS_RULES_TENANT_ID ||
      (grupsTenantId != null && rowTenantId === grupsTenantId)
    );
  }

  return rowTenantId === filterTenantId;
};

export const resolveRulesTenantDisplayName = (
  tenantId: string | null | undefined,
  tenantName: string | null | undefined,
  tenantNameById?: ReadonlyMap<string, string>
): string => {
  const normalizedName = tenantName?.trim();
  if (normalizedName) {
    return normalizedName;
  }

  const resolvedTenantId = tenantId ?? GRUPS_RULES_TENANT_ID;
  const mappedName = tenantNameById?.get(resolvedTenantId);
  if (mappedName) {
    return mappedName;
  }

  return resolvedTenantId;
};
